"use client";

import { useMemo } from "react";
import { Clock } from "lucide-react";
import { InfoTooltip, TOOLTIPS } from "@/features/help";

interface TimeTravelSliderProps {
  /**
   * ISO date strings from all panorama features, used to derive the range.
   * Null/undefined values are excluded automatically.
   */
  allDates: (string | null | undefined)[];
  /** Currently selected upper date bound (empty string = no limit) */
  value: string;
  onChange: (isoDate: string) => void;
}

export default function TimeTravelSlider({
  allDates,
  value,
  onChange,
}: TimeTravelSliderProps) {
  const { minDate, maxDate, minTs, maxTs } = useMemo(() => {
    const valid = allDates
      .filter((d): d is string => !!d)
      .map((d) => new Date(d).getTime())
      .filter((t) => !isNaN(t))
      .sort((a, b) => a - b);

    if (valid.length === 0) {
      const now = Date.now();
      return {
        minDate: new Date(now - 365 * 24 * 3600 * 1000)
          .toISOString()
          .slice(0, 10),
        maxDate: new Date(now).toISOString().slice(0, 10),
        minTs: now - 365 * 24 * 3600 * 1000,
        maxTs: now,
      };
    }
    return {
      minDate: new Date(valid[0]).toISOString().slice(0, 10),
      maxDate: new Date(valid[valid.length - 1]).toISOString().slice(0, 10),
      minTs: valid[0],
      maxTs: valid[valid.length - 1],
    };
  }, [allDates]);

  // Convert slider value (timestamp ms) ↔ ISO date
  const sliderValue = value
    ? Math.min(Math.max(new Date(value).getTime(), minTs), maxTs)
    : maxTs;

  const pct =
    maxTs === minTs ? 100 : ((sliderValue - minTs) / (maxTs - minTs)) * 100;

  const displayDate = value
    ? new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "All time";

  const isFiltered = !!value && value !== maxDate;

  return (
    <div className="relative z-[30] pointer-events-auto w-[200px]">
      <div className="bg-canvas/40 backdrop-blur-2xl border border-ink/10 rounded-2xl px-4 py-3 shadow-2xl text-ink">
        {/* Header (Minimal) */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-tone-400">
            <Clock className="w-3 h-3 text-indigo-400" />
            Timeline
            <InfoTooltip content={TOOLTIPS.timeTravelSlider} placement="right" size="md" />
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-[10px] font-bold ${
                isFiltered ? "text-indigo-300" : "text-tone-500"
              }`}
            >
              {displayDate}
            </span>
            {isFiltered && (
              <button
                onClick={() => onChange("")}
                className="text-[9px] font-black uppercase tracking-tighter text-indigo-400 hover:text-ink transition-colors"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Slider track (Thin) */}
        <div className="relative group">
          <input
            type="range"
            min={minTs}
            max={maxTs}
            step={24 * 3600 * 1000}
            value={sliderValue}
            onChange={(e) => {
              const ts = Number(e.target.value);
              const iso = new Date(ts).toISOString().slice(0, 10);
              onChange(iso === maxDate ? "" : iso);
            }}
            className="w-full h-1 cursor-pointer appearance-none rounded-full bg-ink/10 accent-indigo-500 hover:accent-indigo-400 transition-all"
            style={{
              background: `linear-gradient(to right, #6366f1 ${pct}%, color-mix(in oklab, var(--ink) 5%, transparent) ${pct}%)`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
